import React from "react";
import MedflytRequestNonFlexibleVisitDetails, {
  PatternRequestDetails,
  SinglesRequestDetails,
} from "./MedflytRequestNonFlexibleVisitDetails";
import MedflytRequestFlexibleVisitDetails, {
  FlexibleVisitRequestDetails,
} from "./MedflytRequestFlexibleVisitDetails";
import RejectVisitModal, { RejectVisitModalProps } from "./RejectVisitModal";
import CancelVisitModal, { CancelVisitModalProps } from "./CancelVisitModal";
import VisitRequestedSuccessModal, {
  VisitRequestedSuccessModalProps,
} from "./VisitRequestedSuccessModal";
import { AppModalAction, ErrorModal, LoadingModal } from "../Generic/AppModal/AppModal";
import BaseMedflytRequestVisitModal from "./BaseMedflytRequestModal";
import { DayOfWeek, Duration } from "@js-joda/core";
import {
  CaregiverRequestsVisitRequest,
  FlexibleRequestParamsReq,
  VisitBroadcastId,
  VisitInstanceId,
} from "../../messages";
import RequestVisitModal from "./RequestVisitModal";
import FarVisitWarningModal from "./FarVisitWarningModal";

type MedflytRequestFlexibleVisitModalProps = {
  type: "MedflytRequestFlexibleVisit";
  requestDetails: FlexibleVisitRequestDetails;
  distanceFromCaregiver: number;
  isFarVisit: boolean;
  onClickClose: () => void;
  onClickRequestVisit: (visitRequest: FlexibleRequestParamsReq) => void;
  onClickRequestFarVisit: (distance: number, visitRequest: FlexibleRequestParamsReq) => void;
};

type MedflytRequestVisitModalProps = {
  type: "MedflytRequestVisit";
  requestedInstancesIds: VisitInstanceId[];
  requestDetails: PatternRequestDetails | SinglesRequestDetails;
  visitBroadcastId: VisitBroadcastId;
  distanceFromCaregiver: number;
  isFarVisit: boolean;
  onClickClose: () => void;
  onClickRequestVisit: (
    visitBroadcastId: VisitBroadcastId,
    visitRequest: CaregiverRequestsVisitRequest
  ) => void;
  onClickRequestFarVisit: (
    distance: number,
    visitBroadcastId: VisitBroadcastId,
    visitRequest: CaregiverRequestsVisitRequest
  ) => void;
};

type RequestVisitModalProps = {
  type: "RequestVisit";
  requestedInstancesIds: VisitInstanceId[];
  requestDetails: PatternRequestDetails | SinglesRequestDetails;
  visitBroadcastId: VisitBroadcastId;
  onClickClose: () => void;
  onClickRequestVisit: (
    visitBroadcastId: VisitBroadcastId,
    visitRequest: CaregiverRequestsVisitRequest
  ) => void;
};

type EngagementErrorModalProps = {
  type: "Error";
  title: string;
  message?: string;
  actions: AppModalAction[];
  onClickClose: () => void;
};

type FarVisitWarningModalProps = {
  type: "FarVisitWarning";
  address: string;
  milesFromCaregivers: number;
  onClickConfirm: () => void;
  onClickClose: () => void;
};

interface Props {
  modalProps: CaregiverEngagementModalProps | null;
}

export type ActiveCaregiverEngaementModal =
  | "RejectVisit"
  | "CancelVisit"
  | "RequestVisit"
  | "MedflytRequestFlexibleVisit"
  | "MedflytRequestVisit"
  | "VisitRequestedSuccess"
  | "Error"
  | "Loading"
  | "FarVisitWarning"
  | null;

export type CaregiverEngagementModalProps =
  | MedflytRequestVisitModalProps
  | MedflytRequestFlexibleVisitModalProps
  | RequestVisitModalProps
  | RejectVisitModalProps
  | CancelVisitModalProps
  | VisitRequestedSuccessModalProps
  | EngagementErrorModalProps
  | LoadingModalProps
  | FarVisitWarningModalProps;

export type LoadingModalProps = {
  type: "Loading";
};

const handleRequestMedflytVisit = (modalProps: MedflytRequestVisitModalProps) => {
  return modalProps.isFarVisit
    ? modalProps.onClickRequestFarVisit(
        modalProps.distanceFromCaregiver,
        modalProps.visitBroadcastId,
        {
          requestedInstances: modalProps.requestedInstancesIds,
        }
      )
    : modalProps.onClickRequestVisit(modalProps.visitBroadcastId, {
        requestedInstances: modalProps.requestedInstancesIds,
      });
};

const handleRequestMedflytFlexibleVisit = (modalProps: MedflytRequestFlexibleVisitModalProps) => {
  return modalProps.isFarVisit
    ? modalProps.onClickRequestFarVisit(modalProps.distanceFromCaregiver, {
        days: modalProps.requestDetails.days,
      })
    : modalProps.onClickRequestVisit({ days: modalProps.requestDetails.days });
};

const CaregiverEngagementModals = (props: Props) => {
  const getModal = () => {
    if (props.modalProps === null) {
      return null;
    }

    switch (props.modalProps.type) {
      case "MedflytRequestVisit": {
        const modalProps = props.modalProps;
        const estimatedSalary = getEstimateSalaryPerWeek(modalProps.requestDetails);
        const visitRate = (modalProps.requestDetails.visitRate ?? 0) / 100;

        return (
          <BaseMedflytRequestVisitModal
            isOpen={true}
            visitDetails={
              <MedflytRequestNonFlexibleVisitDetails requestDetails={modalProps.requestDetails} />
            }
            address={modalProps.requestDetails.address}
            estimatedSalaryPerWeek={estimatedSalary}
            visitRate={visitRate}
            showVisitRate={props.modalProps.requestDetails.visitRate !== null}
            onClickClose={modalProps.onClickClose}
            onClickRequest={() => handleRequestMedflytVisit(modalProps)}
          />
        );
      }
      case "MedflytRequestFlexibleVisit": {
        const modalProps = props.modalProps;
        const estimatedSalary = getEstimateSalaryPerWeek(modalProps.requestDetails);
        const visitRate = (modalProps.requestDetails.visitRate ?? 0) / 100;

        return (
          <BaseMedflytRequestVisitModal
            isOpen={true}
            visitDetails={
              <MedflytRequestFlexibleVisitDetails requestDetails={modalProps.requestDetails} />
            }
            address={modalProps.requestDetails.address}
            estimatedSalaryPerWeek={estimatedSalary}
            visitRate={visitRate}
            showVisitRate={props.modalProps.requestDetails.visitRate !== null}
            onClickClose={modalProps.onClickClose}
            onClickRequest={() => handleRequestMedflytFlexibleVisit(modalProps)}
          />
        );
      }
      case "RequestVisit": {
        const modalProps = props.modalProps;
        return (
          <RequestVisitModal
            isOpen={true}
            onClickRequest={() => {
              modalProps.onClickRequestVisit(modalProps.visitBroadcastId, {
                requestedInstances: modalProps.requestedInstancesIds,
              });
            }}
            onClickClose={modalProps.onClickClose}
          />
        );
      }
      case "RejectVisit":
        return <RejectVisitModal isOpen={true} modalProps={props.modalProps} />;
      case "CancelVisit":
        return <CancelVisitModal isOpen={true} modalProps={props.modalProps} />;
      case "VisitRequestedSuccess":
        return <VisitRequestedSuccessModal isOpen={true} modalProps={props.modalProps} />;
      case "Error":
        return (
          <ErrorModal
            isOpen={true}
            actions={props.modalProps.actions}
            title={props.modalProps.title}
            message={props.modalProps.message}
            onRequestClose={props.modalProps.onClickClose}
          />
        );
      case "Loading":
        return <LoadingModal isOpen={true} />;
      case "FarVisitWarning": {
        const modalProps = props.modalProps;
        return (
          <FarVisitWarningModal
            isOpen={true}
            address={modalProps.address}
            milesFromCaregivers={modalProps.milesFromCaregivers}
            onClickClose={modalProps.onClickClose}
            onClickConfirm={modalProps.onClickConfirm}
          />
        );
      }
      default:
        return null;
    }
  };

  return props.modalProps !== null ? <>{getModal()}</> : null;
};

export default CaregiverEngagementModals;

const getEstimateSalaryPerWeek = (
  request: PatternRequestDetails | SinglesRequestDetails | FlexibleVisitRequestDetails
) => {
  if (request.visitRate === null) {
    return 0;
  }

  switch (request.type) {
    case "Pattern":
    case "Flexible": {
      let hours = 0;
      request.days.map((day) => {
        const startTime = day.startTime;
        const endTime = day.endTime;
        hours += endTime.hour() - startTime.hour();

        if (endTime.isBefore(startTime)) {
          hours += 24;
        }
      });

      return (request.visitRate * hours) / 100;
    }
    case "Singles": {
      const hoursByDayOfWeek = new Map<DayOfWeek, number>();
      let hours = 0;

      request.instances.map((instance) => {
        const dayOfWeek = instance.startDateTime.dayOfWeek();
        if (!hoursByDayOfWeek.has(dayOfWeek)) {
          const durationInHours = Duration.between(
            instance.startDateTime,
            instance.endDateTime
          ).toHours();
          hoursByDayOfWeek.set(dayOfWeek, durationInHours);
          hours += durationInHours;
        }
      });

      return (request.visitRate * hours) / 100;
    }
    default:
      console.error("Unknown request type");
      return 0;
  }
};
